import { useAuth } from 'react-oidc-context';
import { useState } from 'react';
import { LogOut, MessageSquare, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import { ChatHistory } from '@/components/chat/ChatHistory';
import { type Session } from '@/types';
import { MobileMenuButton } from '@/components/chat/ChatHistory';
import { ChatContainer } from '@/components/chat/ChatContainer';
import { sessionsService } from '@/services/sessionsService';
import { useChat } from '@/hooks/useChat';

interface ChatPageProps {
    currentSession?: Session
    onSelectSession?: (sessionId: string) => void
    onNewChat?: () => void
}

export default function ChatPage({ currentSession, onNewChat }: ChatPageProps) {
    const auth = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { setMessages } = useChat();
    const [, setCurrentSession] = useState<Session | null>(null);

    // Move handleSelectSession here
    const handleSelectSession = async (sessionId: string) => {
        if (!sessionId) {
            setCurrentSession(null);
            setMessages([]);
            return;
        }
        try {
            const token = auth.user?.access_token;
            const session = await sessionsService.getSession(sessionId, token);
            if (session) {
                setCurrentSession(session);
                setMessages(session.messages || []);
            } else {
                setCurrentSession(null);
                setMessages([]);
            }
        } catch (error) {
            setCurrentSession(null);
            setMessages([]);
        }
    };

    const handleNewChat = () => {
        setCurrentSession(null)
        setMessages([]);
        if (onNewChat) {
            onNewChat()
        }
    }

    const handleLogout = () => {
        auth.signoutRedirect();
    };

    return (
        <div className="h-full flex">
            {/* Sidebar: ChatHistory */}
            <ChatHistory
                onSelectSession={handleSelectSession}
                currentSessionId={currentSession?.id}
            />
            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Fixed Header Bar */}
                <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border flex items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-2">
                        {/* Mobile menu button */}
                        <div className="lg:hidden">
                            <MobileMenuButton />
                        </div>
                        {/* Title */}
                        <img
                            src="/connectwise.png"
                            alt="ConnectWise"
                            className="h-8"
                        />
                    </div>

                    {/* Right side buttons */}
                    <div className="flex items-center gap-0">
                        {/* New Chat Button */}
                        <Button
                            onClick={handleNewChat}
                            variant="ghost"
                            size="sm"
                            className="flex items-center gap-2"
                        >
                            <MessageSquare className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="h-9 w-9"
                            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                        >
                            {theme === 'light' ? (
                                <Moon className="h-4 w-4 transition-all" />
                            ) : (
                                <Sun className="h-4 w-4 transition-all" />
                            )}
                            <span className="sr-only">Toggle theme</span>
                        </Button>
                        <Button
                            onClick={handleLogout}
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                        >
                            <LogOut className="h-4 w-4 transition-all" />
                        </Button>
                    </div>
                </header>
                {/* Chat Content - no additional padding needed since App.tsx handles header offset */}
                <div className="h-full">
                    <ChatContainer />
                </div>
            </div>
        </div>
    );
}